import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { Modal, ModalTitle, ModalBody, ModalFooter } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Input } from '../../Input';
import { GenericContactSupportError } from '../../GenericContactSupportError';
import { formatPhoneNumberIncrementally, phoneNumberUnformatted } from '../../../utils';
import { PHONE_VALIDATION_REGEX } from '../../../constants';
import { client, ErrorResponse } from '../../../api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

interface AddSinglePhoneNumberModalProps {
  /** Callback to close the modal */
  onClose: () => void;
  /** Existing phone numbers to check for duplicates */
  existingNumbers: string[];
}

interface FormData {
  phoneNumber: string;
}

/**
 * Modal for adding a single office phone number.
 */
const AddSinglePhoneNumberModal: React.FC<AddSinglePhoneNumberModalProps> = ({ onClose, existingNumbers }) => {
  const queryClient = useQueryClient();
  const [showPhoneAlreadyRegisteredError, setShowPhoneAlreadyRegisteredError] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      phoneNumber: '',
    },
  });

  const addPhoneNumber = useMutation<unknown, AxiosError<ErrorResponse>, string>({
    mutationFn: async (phoneNumber) => {
      return client.patch('settings/phone-numbers', { phone_numbers: [phoneNumber] }).then((res) => res.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['office-phone-numbers'] });
      toast.custom(() => <Toast variant="success">Phone number added</Toast>);
      onClose();
    },
    onError: (error) => {
      const errorDetail = error.response?.data?.detail;
      const message = error.response?.data?.error || 'Error adding phone number';

      // Check if this is the phone number already registered error
      if (errorDetail === 'PHONE_NUMBER_ALREADY_REGISTERED') {
        setShowPhoneAlreadyRegisteredError(true);
      } else {
        // Unexpected error - log to Sentry
        Sentry.captureException(error);
        toast.custom(() => <StandardErrorToast />);
        console.error(message);
      }
    },
  });

  const watchedValues = watch();
  const hasInput = watchedValues.phoneNumber?.trim();

  const onSubmit = (data: FormData) => {
    const cleanedPhoneNumber = phoneNumberUnformatted(data.phoneNumber.trim());

    // Clear any previous backend error
    setShowPhoneAlreadyRegisteredError(false);

    if (existingNumbers.includes(cleanedPhoneNumber)) {
      setError('phoneNumber', { message: 'This phone number has already been added' });
      return;
    }
    addPhoneNumber.mutate(cleanedPhoneNumber);
  };

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Add office number</ModalTitle>
      <ModalBody>
        <form id="add-phone-number-form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name="phoneNumber"
            rules={{
              required: { value: true, message: 'Phone number is required' },
              pattern: {
                value: PHONE_VALIDATION_REGEX,
                message: 'Please enter a valid phone number',
              },
            }}
            render={({ field }) => (
              <Input
                type="tel"
                label="Phone number"
                placeholder="e.g. (555) 123-4567"
                hasError={!!errors.phoneNumber || showPhoneAlreadyRegisteredError}
                disabled={addPhoneNumber.isPending}
                aria-describedby={
                  errors.phoneNumber
                    ? 'phone-number-validation-error'
                    : showPhoneAlreadyRegisteredError
                      ? 'phone-number-backend-error'
                      : undefined
                }
                {...field}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatPhoneNumberIncrementally(e.target.value);
                  field.onChange(formatted);
                  // Clear backend error when user starts typing
                  if (showPhoneAlreadyRegisteredError) {
                    setShowPhoneAlreadyRegisteredError(false);
                  }
                }}
              />
            )}
          />
          {errors.phoneNumber && (
            <ErrorMessage id="phone-number-validation-error">{errors.phoneNumber.message}</ErrorMessage>
          )}
          {showPhoneAlreadyRegisteredError && <GenericContactSupportError id="phone-number-backend-error" />}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose} variant="tertiary" size="sm" disabled={addPhoneNumber.isPending}>
          Back
        </Button>
        <Button
          type="submit"
          form="add-phone-number-form"
          size="sm"
          disabled={!hasInput}
          isLoading={addPhoneNumber.isPending}
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddSinglePhoneNumberModal;
